/**
 * VersionHistoryDrawer — browse crm_campaign_template_versions for a template
 * and roll back to any previous version.
 *
 * Every save/edit snapshots the OUTGOING state via a DB trigger (see
 * supabase/migrations/20261023_campaign_template_versioning.sql), so each row
 * here is that version's final {template, nodes} state. Restoring:
 *   1. restore_campaign_template RPC overwrites the content fields + node set
 *      and bumps version to current + 1 (the pre-restore state is snapshotted
 *      too, so a restore is itself undoable), then
 *   2. the syncCampaignUpdates callable propagates the restored nodes to every
 *      subscriber's clone — identical to a normal Builder save.
 *
 * Gate the entry points with canEditTemplate; this drawer assumes the caller
 * may edit the template.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Badge,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRotateCcw, FiClock } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchTemplateVersions, restoreTemplateVersion } from '../../../api/supabase/automations';

const fbFunctions = getFunctions(undefined, 'us-east1');

export default function VersionHistoryDrawer({ isOpen, onClose, template, onRestored }) {
  const toast = useToast();
  const rowBorder = useColorModeValue('gray.200', 'gray.600');
  const subText = useColorModeValue('gray.500', 'gray.400');
  const cancelRef = useRef();

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // version row pending restore
  const [restoring, setRestoring] = useState(false);
  // Tracked locally: a restore bumps the live version (current + 1) but the
  // template prop stays stale until the parent lists reload.
  const [currentVersion, setCurrentVersion] = useState(template?.version || 1);

  useEffect(() => {
    setCurrentVersion(template?.version || 1);
  }, [template?.id, template?.version]);

  const load = useCallback(async () => {
    if (!template?.id) return;
    setLoading(true);
    try {
      const rows = await fetchTemplateVersions(template.id);
      setVersions(rows || []);
    } catch (err) {
      console.error('[VersionHistoryDrawer] load error:', err);
      toast({ title: 'Failed to load history', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [template?.id, toast]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleRestore = async () => {
    if (!confirmTarget || !template?.id) return;
    setRestoring(true);
    try {
      await restoreTemplateVersion(template.id, confirmTarget.version);
      // Propagate the restored node set to every subscriber clone — same
      // pipeline as a Builder save (version was bumped by the RPC).
      try {
        const syncFn = httpsCallable(fbFunctions, 'syncCampaignUpdates');
        await syncFn({ templateId: template.id });
      } catch (syncErr) {
        console.warn('[VersionHistoryDrawer] sync to subscribers failed:', syncErr.message);
      }
      toast({
        title: `Restored v${confirmTarget.version}`,
        description: 'Subscribers were updated to the restored workflow.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setConfirmTarget(null);
      // The RPC set the live version to current + 1
      setCurrentVersion((v) => v + 1);
      await load();
      onRestored?.();
    } catch (err) {
      console.error('[VersionHistoryDrawer] restore error:', err);
      toast({ title: 'Restore failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <HStack spacing={2}>
              <FiClock />
              <Text fontSize="lg" fontWeight="600">Version History</Text>
            </HStack>
            <Text fontSize="xs" color={subText} fontWeight="normal" mt={1}>
              {template?.name} — currently v{currentVersion}
            </Text>
          </DrawerHeader>
          <DrawerBody py={4}>
            {loading ? (
              <Flex justify="center" py={10}><Spinner /></Flex>
            ) : !versions.length ? (
              <Text fontSize="sm" color={subText} textAlign="center" py={8}>
                No snapshots yet. A snapshot is written automatically every time
                this campaign is saved.
              </Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {versions.map((v) => {
                  const snapTemplate = v.snapshot?.template || {};
                  const nodeCount = Array.isArray(v.snapshot?.nodes) ? v.snapshot.nodes.length : 0;
                  const isCurrent = v.version === currentVersion;
                  return (
                    <Flex
                      key={v.id}
                      justify="space-between"
                      align="center"
                      py={3}
                      borderBottom="1px solid"
                      borderColor={rowBorder}
                      gap={3}
                    >
                      <VStack align="start" spacing={0.5} flex={1} minW={0}>
                        <HStack spacing={2}>
                          <Badge colorScheme={isCurrent ? 'green' : 'gray'} fontSize="xs">
                            v{v.version}
                          </Badge>
                          {isCurrent && (
                            <Badge colorScheme="green" variant="subtle" fontSize="2xs">Current</Badge>
                          )}
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                            {snapTemplate.name || template?.name}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color={subText}>
                          {nodeCount} step{nodeCount === 1 ? '' : 's'} ·{' '}
                          {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                        </Text>
                      </VStack>
                      {!isCurrent && (
                        <Button
                          leftIcon={<FiRotateCcw />}
                          size="xs"
                          variant="outline"
                          onClick={() => setConfirmTarget(v)}
                        >
                          Restore
                        </Button>
                      )}
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <AlertDialog
        isOpen={!!confirmTarget}
        leastDestructiveRef={cancelRef}
        onClose={() => !restoring && setConfirmTarget(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Restore v{confirmTarget?.version}?
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="start" spacing={2}>
                <Text fontSize="sm">
                  &ldquo;{template?.name}&rdquo; will be rolled back to the workflow it had at
                  v{confirmTarget?.version}, and every subscribed user&apos;s campaign updates
                  to match.
                </Text>
                <Text fontSize="sm" color={subText}>
                  The current state (v{currentVersion}) stays in history, so this
                  can be undone by restoring it back.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} size="sm" onClick={() => setConfirmTarget(null)} isDisabled={restoring}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                size="sm"
                ml={3}
                leftIcon={<FiRotateCcw />}
                onClick={handleRestore}
                isLoading={restoring}
              >
                Restore
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
